import cloneDeep from 'lodash/cloneDeep'
import get from 'lodash/get'
import set from 'lodash/set'
import { useState } from 'react'
import { getRequirementId, isRequirementRef, toRequirementRef } from '../../../../../util/groupRequirement.util'
import { INPUT_POLICY_PATH, isGroupInput } from '../../policy_feature/inputs'

// Inline create-definition (mail nickname, preferred domain, …) lives next to the policy body
// rather than in a separate table — a requirement is still identified by name (`req:<name>`).
export const GROUP_REQUIREMENT_DEFINITIONS_KEY = 'groupRequirementDefinitions'

// Pure: returns a new body with any now-unreferenced inline definitions dropped, rather than
// mutating the one it's handed — the caller reassigns its own (already freshly cloned) body to
// the result.
const pruneInlineDefinitions = (body, groupFields) => {
  const definitions = body[GROUP_REQUIREMENT_DEFINITIONS_KEY]
  if (!definitions) {
    return body
  }

  const used = new Set()
  groupFields.forEach(({ path }) => {
    ;(get(body, path) || []).forEach((value) => {
      if (isRequirementRef(value)) {
        used.add(getRequirementId(value))
      }
    })
  })

  const next = Object.fromEntries(Object.entries(definitions).filter(([id]) => used.has(id)))
  const { [GROUP_REQUIREMENT_DEFINITIONS_KEY]: omitted, ...rest } = body
  return Object.keys(next).length ? { ...rest, [GROUP_REQUIREMENT_DEFINITIONS_KEY]: next } : rest
}

// CA's own group-bearing fields, as a { key, path } list — the default `groupFields` for every
// caller that predates this being a parameter (i.e. every CA caller), so their behavior is
// unchanged. A different template family (e.g. Defender) passes its own list instead of importing
// CA's inputs.js.
const CA_GROUP_FIELDS = Object.keys(INPUT_POLICY_PATH)
  .filter(isGroupInput)
  .map((key) => ({ key, path: INPUT_POLICY_PATH[key] }))

const deriveInitialState = (policyBody, groupFields) => {
  const autoModeInputs = []
  const requirementIds = {}

  groupFields.forEach(({ key, path }) => {
    const values = get(policyBody, path) || []
    const references = values.filter(isRequirementRef).map(getRequirementId)
    if (references.length) {
      autoModeInputs.push(key)
      requirementIds[key] = references
    }
  })

  return { autoModeInputs, requirementIds }
}

// Exposed so a caller can seed a field-visibility list (e.g. a "which rows to show" state
// living outside this hook) with fields that already resolve via a stored requirement, without
// waiting a render for the hook itself to report it.
export const getAutoModeGroupInputs = (policyBody, groupFields = CA_GROUP_FIELDS) =>
  deriveInitialState(policyBody, groupFields).autoModeInputs

// Owns, for a template's group-bearing fields, whether each one is resolved automatically from
// one or more stored group requirements or still manually prompted at deploy (the pre-existing,
// backward-compatible default) — and keeps the field's raw JSON value (its "group:<id>" references)
// in sync with whatever is attached. `groupFields` (a { key, path } list) defaults to CA's own
// fields; a different template family passes its own instead.
export default function useGroupFieldRequirements({
  initialPolicyBody,
  policyBody,
  setPolicyBody,
  groupFields = CA_GROUP_FIELDS,
}) {
  const [{ autoModeInputs, requirementIds }, setState] = useState(() =>
    deriveInitialState(initialPolicyBody, groupFields),
  )
  const pathByField = Object.fromEntries(groupFields.map(({ key, path }) => [key, path]))

  const isAutoMode = (input) => autoModeInputs.includes(input)
  const getAttachedRequirementIds = (input) => requirementIds[input] || []

  const writeFieldValue = (input, values, mutatePolicyBody) => {
    const updated = cloneDeep(policyBody) || {}
    set(updated, pathByField[input], values)
    mutatePolicyBody?.(updated)
    setPolicyBody(pruneInlineDefinitions(updated, groupFields))
  }

  const toggleAutoMode = (input, enabled) => {
    setState((current) => ({
      autoModeInputs: enabled
        ? [...current.autoModeInputs, input]
        : current.autoModeInputs.filter((key) => key !== input),
      requirementIds: enabled ? current.requirementIds : { ...current.requirementIds, [input]: [] },
    }))

    if (!enabled) {
      writeFieldValue(input, [])
    }
  }

  // The single source of truth for "which requirements are on this field" — matches the
  // MultiSelectPillList/MultiSelectSearch contract of handing back the whole new selection on
  // every change, add or remove alike.
  const setAttachedRequirementIds = (input, updatedIds, mutatePolicyBody) => {
    setState((current) => ({ ...current, requirementIds: { ...current.requirementIds, [input]: updatedIds } }))
    writeFieldValue(input, updatedIds.map(toRequirementRef), mutatePolicyBody)
  }

  // Replaces hook state from a policy body that was already written (for example after binding a
  // copied policy's selections by name). Does not write the body again.
  const hydrate = (nextPolicyBody) => {
    setState(deriveInitialState(nextPolicyBody, groupFields))
  }

  return { isAutoMode, getAttachedRequirementIds, toggleAutoMode, setAttachedRequirementIds, hydrate }
}
